import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchHolidayList,
  updateHolidayByAdmin,
} from "../redux/actions/holidayActions";
import {
  FiCalendar,
  FiEdit,
  FiX,
  FiRefreshCw,
  FiArrowLeft,
  FiInfo,
  FiMapPin,
  FiPlus,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const ViewHoliday = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { holidays, loading, error } = useSelector((state) => state.holiday);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [formData, setFormData] = useState({
    holiday_name: "",
    holiday_date: "",
    description: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchHolidayList());
  }, [dispatch]);

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentHolidays = holidays.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(holidays.length / itemsPerPage);

  const handleEditClick = (holiday) => {
    setSelectedHoliday(holiday);
    setFormData({
      holiday_name: holiday.holiday_name || "",
      holiday_date: holiday.holiday_date
        ? holiday.holiday_date.slice(0, 10)
        : "",
      description: holiday.description || "",
    });
    setEditModalOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedHoliday) return;

    const updatedData = {
      id: selectedHoliday.id,
      ...formData,
    };

    dispatch(updateHolidayByAdmin(updatedData)).then(() => {
      setEditModalOpen(false);
      setSelectedHoliday(null);
      dispatch(fetchHolidayList()); // Refresh the list
    });
  };

  const isUpcomingHoliday = (date) => {
    const today = new Date();
    const holidayDate = new Date(date);
    return holidayDate >= today;
  };

  const getHolidayStatus = (date) => {
    const today = new Date();
    const holidayDate = new Date(date);
    const diffTime = holidayDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0)
      return {
        text: "Today",
        color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      };
    if (diffDays === 1)
      return {
        text: "Tomorrow",
        color:
          "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
      };
    if (diffDays > 0 && diffDays <= 7)
      return {
        text: "Next Week",
        color:
          "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
      };
    if (diffDays > 0)
      return {
        text: "Upcoming",
        color:
          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      };
    return {
      text: "Past",
      color: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Loading Holidays...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          {/* Left side */}
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Dashboard
          </button>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard/create-holiday")}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-xl transition-all shadow-md"
            >
              <FiPlus className="text-lg" />
              Create Holiday
            </button>

            <button
              onClick={() => dispatch(fetchHolidayList())}
              disabled={loading}
              className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 py-2 px-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
            >
              <FiRefreshCw
                className={`text-lg ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiCalendar className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Holiday List
                </h1>
                <p className="text-blue-100">
                  View and manage all organization holidays
                </p>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiInfo className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      Error loading holidays
                    </p>
                    <p className="text-red-600 dark:text-red-400 text-sm">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <FiCalendar className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">
                      Total Holidays
                    </p>
                    <p className="text-blue-900 dark:text-blue-100 text-2xl font-bold">
                      {holidays.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <FiCalendar className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-green-600 dark:text-green-400 text-sm font-medium">
                      Upcoming
                    </p>
                    <p className="text-green-900 dark:text-green-100 text-2xl font-bold">
                      {
                        holidays.filter((h) =>
                          isUpcomingHoliday(h.holiday_date),
                        ).length
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                    <FiCalendar className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-orange-600 dark:text-orange-400 text-sm font-medium">
                      This Week
                    </p>
                    <p className="text-orange-900 dark:text-orange-100 text-2xl font-bold">
                      {
                        holidays.filter((h) => {
                          const today = new Date();
                          const holidayDate = new Date(h.holiday_date);
                          const diffTime = holidayDate - today;
                          const diffDays = Math.ceil(
                            diffTime / (1000 * 60 * 60 * 24),
                          );
                          return diffDays >= 0 && diffDays <= 7;
                        }).length
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <FiMapPin className="text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-purple-600 dark:text-purple-400 text-sm font-medium">
                      This Month
                    </p>
                    <p className="text-purple-900 dark:text-purple-100 text-2xl font-bold">
                      {
                        holidays.filter((h) => {
                          const today = new Date();
                          const holidayDate = new Date(h.holiday_date);
                          return (
                            holidayDate.getMonth() === today.getMonth() &&
                            holidayDate.getFullYear() === today.getFullYear()
                          );
                        }).length
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                      <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FiCalendar className="text-sm" />
                          Holiday Name
                        </div>
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {currentHolidays.length > 0 ? (
                      currentHolidays.map((holiday, index) => {
                        const status = getHolidayStatus(holiday.holiday_date);
                        return (
                          <tr
                            key={holiday.id || index}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <td className="py-4 px-6">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {holiday.holiday_name || "-"}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="text-sm text-gray-900 dark:text-white">
                                {holiday.holiday_date
                                  ? new Date(
                                      holiday.holiday_date,
                                    ).toLocaleDateString("en-IN", {
                                      weekday: "short",
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : "-"}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${status.color}`}
                              >
                                {status.text}
                              </span>
                            </td>
                            <td className="py-4 px-6">
                              <div className="text-sm text-gray-900 dark:text-white max-w-md">
                                {holiday.description || "-"}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <button
                                onClick={() => handleEditClick(holiday)}
                                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-xl transition-colors text-sm"
                              >
                                <FiEdit className="text-sm" />
                                Edit
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="py-8 px-6 text-center">
                          <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                            <FiCalendar className="text-3xl mb-2 opacity-50" />
                            <p className="text-lg font-medium">
                              No holidays found
                            </p>
                            <p className="text-sm">
                              There are no holidays scheduled at the moment.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Showing{" "}
                      <span className="font-medium">
                        {indexOfFirstItem + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(indexOfLastItem, holidays.length)}
                      </span>{" "}
                      of <span className="font-medium">{holidays.length}</span>{" "}
                      holidays
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentPage === 1}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(prev + 1, totalPages),
                          )
                        }
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Card */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
            💡 Holiday Management
          </h3>
          <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
            <li>
              • Holidays are automatically sorted by date for easy reference
            </li>
            <li>
              • Status indicators show whether holidays are upcoming or past
            </li>
            <li>• Use the edit feature to update holiday details as needed</li>
            <li>
              • Refresh the list to see the most current holiday information
            </li>
            <li>• Upcoming holidays are highlighted for better visibility</li>
          </ul>
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit Holiday
              </h2>
              <button
                onClick={() => setEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Holiday Name
                </label>
                <input
                  type="text"
                  value={formData.holiday_name}
                  onChange={(e) =>
                    setFormData({ ...formData, holiday_name: e.target.value })
                  }
                  className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                  placeholder="Enter holiday name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Holiday Date
                </label>
                <input
                  type="date"
                  value={formData.holiday_date}
                  onChange={(e) =>
                    setFormData({ ...formData, holiday_date: e.target.value })
                  }
                  className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition resize-none"
                  rows="3"
                  placeholder="Enter holiday description"
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors"
              >
                Update Holiday
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewHoliday;
